if (!customElements.get('promotional-slideshow-component')) {
  customElements.define(
    'promotional-slideshow-component',
    class PromotionalSlideshowComponent extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 0; // Track current slide index
        this.autoplayTimer = null; // Track autoplay timer
        this.isInitialized = false; // Track initialization state
        
        // Initialize when DOM is fully loaded
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
          setTimeout(() => this.init(), 1);
        } else {
          document.addEventListener('DOMContentLoaded', () => this.init());
        }
      }

      init() {
        // Get all slides
        this.slides = this.querySelectorAll('.swiper-slide');
        this.totalSlides = this.slides.length;
        
        if (this.totalSlides === 0) {
          return;
        }

        // Reset any active classes first - this helps with proper initial state
        this.slides.forEach(slide => {
          slide.classList.remove('swiper-slide-active');
          slide.style.opacity = '0'; // Hide all slides initially
          slide.style.zIndex = '0';
        });
        
        // Immediately make the first slide visible
        if (this.slides[0]) {
          this.slides[0].classList.add('swiper-slide-active');
          this.slides[0].style.opacity = '1';
          this.slides[0].style.zIndex = '10';
          // Activate the content of the first slide
          this.activateContent(this.slides[0]);
        }
        
        // Get navigation elements
        this.nextButton = this.querySelector('.swiper-button-next');
        this.prevButton = this.querySelector('.swiper-button-prev');
        this.pagination = this.querySelector('.swiper-pagination');
        
        // Add our own custom navigation handlers
        if (this.nextButton) {
          this.nextButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.goToNextSlide();
            this.pauseAutoplay(); // Pause autoplay on manual navigation
          });
        }
        
        if (this.prevButton) {
          this.prevButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.goToPrevSlide();
            this.pauseAutoplay(); // Pause autoplay on manual navigation
          });
        }
        
        // Setup pagination if it exists
        if (this.pagination && this.totalSlides > 1) {
          this.setupPagination();
        }
        
        // Setup autoplay if enabled
        this.setupAutoplay();
        
        // Initialize Swiper but use our custom navigation
        this.initializeSwiper();
        
        // Show the first slide immediately
        this.goToSlide(0, true); // Force first slide to show, even if it's already the current index
        
        // Set color scheme for controls based on first slide
        if (this.slides[0]) {
          this.updateControlsColor(this.slides[0]);
        }
        
        // Remove loading state
        this.removeAttribute('data-media-loading');
        
        // Add pause/resume autoplay on hover
        this.addEventListener('mouseenter', () => this.pauseAutoplay());
        this.addEventListener('mouseleave', () => this.resumeAutoplay());
        
        // Start autoplay if enabled, with a small delay to ensure first slide is visible
        setTimeout(() => {
          this.isInitialized = true;
          this.startAutoplay();
        }, 100);
      }
      
      setupAutoplay() {
        // Check if autoplay is enabled and get delay
        this.autoplayEnabled = false;
        this.autoplayDelay = 0;
        
        if (this.hasAttribute('data-autoplay')) {
          const autoplayDelay = parseInt(this.dataset.autoplay);
          if (autoplayDelay > 0) {
            this.autoplayEnabled = true;
            this.autoplayDelay = autoplayDelay;
          }
        }
      }
      
      startAutoplay() {
        // Start autoplay if enabled and multiple slides
        if (this.autoplayEnabled && this.totalSlides > 1 && this.isInitialized) {
          this.clearAutoplayTimer(); // Clear any existing timer
          
          this.autoplayTimer = setInterval(() => {
            this.goToNextSlide();
          }, this.autoplayDelay);
        }
      }
      
      pauseAutoplay() {
        this.clearAutoplayTimer();
      }
      
      resumeAutoplay() {
        if (this.autoplayEnabled && this.isInitialized) {
          this.startAutoplay();
        }
      }
      
      clearAutoplayTimer() {
        if (this.autoplayTimer) {
          clearInterval(this.autoplayTimer);
          this.autoplayTimer = null;
        }
      }
      
      initializeSwiper() {
        try {
          // Direct approach using Swiper syntax
          const options = {
            slidesPerView: 1,
            spaceBetween: 0,
            loop: false, // We'll handle looping ourselves
            effect: 'fade', 
            autoHeight: false,
            initialSlide: 0,
            fadeEffect: {
              crossFade: true
            },
            
            // We'll manage navigation ourselves
            navigation: false, 
            pagination: false,
            
            // Disable swiping/dragging - we'll use button navigation only
            allowTouchMove: false,
            
            on: {
              init: () => {
                // console.log('Swiper init event fired');
              }
            }
          };
          
          // Try direct initialization first if Swiper is globally available
          if (typeof Swiper !== 'undefined') {
            this.swiper = new Swiper(this, options);
          } 
          // Otherwise try theme-specific initialization
          else if (window.FoxTheme && window.FoxTheme.Carousel) {
            this.carousel = new window.FoxTheme.Carousel(this, options);
            this.carousel.init();
            this.swiper = this.carousel.slider;
          }
        } catch (error) {
          console.error('Error initializing slider:', error);
        }
      }
      
      setupPagination() {
        // Clear existing pagination
        this.pagination.innerHTML = '';
        
        // Add bullet for each slide
        for (let i = 0; i < this.totalSlides; i++) {
          const bullet = document.createElement('span');
          bullet.classList.add('swiper-pagination-bullet');
          if (i === 0) {
            bullet.classList.add('swiper-pagination-bullet-active');
          }
          
          bullet.addEventListener('click', () => {
            this.goToSlide(i);
            this.pauseAutoplay(); // Pause autoplay on manual navigation
          });
          
          this.pagination.appendChild(bullet);
        }
      }
      
      goToSlide(index, force = false) {
        
        // Ensure index is valid
        if (index < 0) {
          index = this.totalSlides - 1;
        } else if (index >= this.totalSlides) {
          index = 0;
        }
        
        // Don't do anything if trying to go to current slide, unless forced
        if (index === this.currentIndex && !force) {
          return;
        }
        
        // Update current index
        this.currentIndex = index;
        
        // Hide all slides
        this.slides.forEach((slide, i) => {
          slide.classList.remove('swiper-slide-active');
          slide.style.opacity = '0';
          slide.style.zIndex = '0';
        });
        
        // Show the current slide
        const currentSlide = this.slides[index];
        if (!currentSlide) {
          return;
        }
        
        currentSlide.classList.add('swiper-slide-active');
        currentSlide.style.opacity = '1';
        currentSlide.style.zIndex = '10';
        
        // Update pagination
        if (this.pagination) {
          const bullets = this.pagination.querySelectorAll('.swiper-pagination-bullet');
          bullets.forEach((bullet, i) => {
            if (i === index) {
              bullet.classList.add('swiper-pagination-bullet-active');
            } else {
              bullet.classList.remove('swiper-pagination-bullet-active');
            }
          });
        }
        
        // Update controls color
        this.updateControlsColor(currentSlide);
        
        // Activate content
        this.activateContent(currentSlide);
        
        // Also try to use the swiper API as a backup
        if (this.swiper && typeof this.swiper.slideTo === 'function') {
          try {
            this.swiper.slideTo(index, 0, false);
          } catch (e) {
            console.warn('Error using swiper.slideTo:', e);
          }
        }
      }
      
      goToNextSlide() {
        this.goToSlide(this.currentIndex + 1);
      }
      
      goToPrevSlide() {
        this.goToSlide(this.currentIndex - 1);
      }
      
      activateContent(slide) {
        if (!slide) return;
        
        // Ensure the slide is visible
        slide.style.opacity = '1';
        
        // Handle videos
        if (slide.dataset.type === 'video') {
          const video = slide.querySelector('video-element');
          if (video && typeof video.play === 'function') {
            video.play();
          }
        }
        
        // Handle animations
        const animations = slide.querySelectorAll('motion-element');
        animations.forEach(animation => {
          if (animation && typeof animation.refreshAnimation === 'function') {
            animation.refreshAnimation();
          }
        });
      }
      
      updateControlsColor(activeSlide) {
        if (!activeSlide || !activeSlide.dataset) return;
        
        const colorScheme = activeSlide.dataset.colorScheme;
        if (!colorScheme) return;
        
        const controls = this.querySelector('.swiper-controls');
        if (!controls) return;
        
        // Remove any existing color schemes
        const colorClasses = Array.from(controls.classList)
          .filter(cls => cls.startsWith('color-'));
          
        colorClasses.forEach(cls => controls.classList.remove(cls));
        
        // Add the new color scheme
        controls.classList.add(colorScheme);
        
        // Update navigation button colors if custom colors are set
        const nextButtonColor = activeSlide.dataset.nextButtonColor;
        const prevButtonColor = activeSlide.dataset.prevButtonColor;
        
        if (nextButtonColor && this.nextButton) {
          this.nextButton.style.color = nextButtonColor;
        }
        
        if (prevButtonColor && this.prevButton) {
          this.prevButton.style.color = prevButtonColor;
        }
      }
      
      // Clean up when element is removed from DOM
      disconnectedCallback() {
        this.clearAutoplayTimer();
      }
    }
  );
}
